import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  MapPin, Plus, X, Loader2, ShoppingCart, ArrowRight, Plane,
  Database, Clock, ChevronDown, Sparkles, Route, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DATA_OPTIONS = [
  { value: 'low', label: '1 GB', icon: '📶' },
  { value: 'medium', label: '3-5 GB', icon: '📡' },
  { value: 'high', label: '10+ GB', icon: '🌐' },
];

export default function TripPlanner({ onNavigate }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [destinations, setDestinations] = useState(['', '']);
  const [daysPerStop, setDaysPerStop] = useState(7);
  const [dataNeed, setDataNeed] = useState('medium');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addedAll, setAddedAll] = useState(false);

  const addDestination = () => {
    if (destinations.length < 10) {
      setDestinations([...destinations, '']);
    }
  };

  const removeDestination = (idx) => {
    if (destinations.length > 2) {
      setDestinations(destinations.filter((_, i) => i !== idx));
    }
  };

  const updateDestination = (idx, val) => {
    const updated = [...destinations];
    updated[idx] = val;
    setDestinations(updated);
  };

  const optimize = useCallback(async () => {
    const valid = destinations.filter(d => d.trim());
    if (valid.length < 2) {
      toast.error(t('tripPlanner.minDestinations', 'Inserisci almeno 2 destinazioni'));
      return;
    }
    setLoading(true);
    setResults(null);
    setAddedAll(false);
    try {
      const res = await axios.post(`${API}/trip-planner/optimize`, {
        destinations: valid,
        days_per_stop: daysPerStop,
        data_need: dataNeed,
      });
      setResults(res.data);
    } catch {
      toast.error(t('tripPlanner.error', 'Errore nell\'ottimizzazione'));
    }
    setLoading(false);
  }, [destinations, daysPerStop, dataNeed, t]);

  const handleAddAllToCart = async () => {
    if (!results) return;
    const validStops = results.stops.filter(s => s.found && s.recommended_plan);
    let added = 0;
    for (const stop of validStops) {
      try {
        await addToCart({
          product_id: stop.product_id,
          id: stop.product_id,
          name: stop.product_name,
          price: parseFloat(stop.recommended_plan.price),
          country_code: stop.country_code,
          variation_id: stop.recommended_plan.id,
          variation_name: `${stop.recommended_plan.data} / ${stop.recommended_plan.duration}`,
        });
        added++;
      } catch { /* ignore */ }
    }
    if (added > 0) {
      setAddedAll(true);
      toast.success(t('tripPlanner.addedAll', `${added} eSIM aggiunte al carrello!`).replace('${added}', added));
    }
  };

  return (
    <div className="min-h-screen bg-[#070b14] pt-4 pb-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-4">
            <Route className="w-4 h-4 text-teal-400" />
            <span className="text-teal-400 text-xs font-semibold uppercase tracking-wider">{t('tripPlanner.badge', 'Trip Planner')}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="trip-planner-title">
            {t('tripPlanner.title', 'Pianifica il tuo Viaggio')}
          </h1>
          <p className="text-slate-400 text-sm max-w-lg mx-auto">
            {t('tripPlanner.subtitle', 'Inserisci le tappe del tuo itinerario e troveremo il piano eSIM ottimale per ogni destinazione')}
          </p>
        </motion.div>

        {/* Input Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6 mb-6" data-testid="trip-planner-input"
        >
          {/* Destinations */}
          <div className="mb-5">
            <label className="flex items-center gap-2 text-white text-sm font-semibold mb-3">
              <MapPin className="w-4 h-4 text-teal-400" />
              {t('tripPlanner.destinations', 'Tappe del Viaggio')}
            </label>
            <div className="space-y-2">
              {destinations.map((dest, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-500/20 text-teal-400 text-xs font-bold flex-shrink-0">
                    {idx + 1}
                  </div>
                  {idx > 0 && (
                    <div className="absolute -mt-7 ml-3.5 w-px h-3 bg-teal-500/20" />
                  )}
                  <input
                    type="text"
                    value={dest}
                    onChange={(e) => updateDestination(idx, e.target.value)}
                    placeholder={idx === 0 ? t('tripPlanner.firstStop', 'Prima tappa (es. Roma)')
                      : idx === destinations.length - 1 ? t('tripPlanner.lastStop', 'Ultima tappa (es. Londra)')
                      : t('tripPlanner.middleStop', 'Tappa intermedia (es. Parigi)')}
                    className="flex-1 bg-white/5 border border-white/10 text-white placeholder-slate-600 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500/40 transition-colors"
                    data-testid={`destination-input-${idx}`}
                  />
                  {destinations.length > 2 && (
                    <button onClick={() => removeDestination(idx)} className="text-slate-600 hover:text-red-400 transition-colors p-1" data-testid={`remove-dest-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {destinations.length < 10 && (
              <button onClick={addDestination} className="mt-2 flex items-center gap-1.5 text-teal-400/70 hover:text-teal-400 text-xs transition-colors" data-testid="add-destination-btn">
                <Plus className="w-3.5 h-3.5" /> {t('tripPlanner.addStop', 'Aggiungi tappa')}
              </button>
            )}
          </div>

          {/* Options Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {/* Days per stop */}
            <div>
              <label className="flex items-center gap-2 text-slate-400 text-xs font-medium mb-2">
                <Clock className="w-3.5 h-3.5" />
                {t('tripPlanner.daysPerStop', 'Giorni per tappa')}
              </label>
              <div className="flex items-center gap-2">
                {[3, 5, 7, 14, 30].map(d => (
                  <button
                    key={d}
                    onClick={() => setDaysPerStop(d)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      daysPerStop === d ? 'bg-teal-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                    data-testid={`days-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            {/* Data need */}
            <div>
              <label className="flex items-center gap-2 text-slate-400 text-xs font-medium mb-2">
                <Database className="w-3.5 h-3.5" />
                {t('tripPlanner.dataNeed', 'Consumo dati')}
              </label>
              <div className="flex items-center gap-2">
                {DATA_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setDataNeed(opt.value)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      dataNeed === opt.value ? 'bg-teal-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                    data-testid={`data-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Optimize Button */}
          <button
            onClick={optimize}
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-black font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="optimize-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t('tripPlanner.optimizing', 'Ottimizzazione in corso...')}</>
            ) : (
              <><Sparkles className="w-4 h-4" /> {t('tripPlanner.findBestPlans', 'Trova i Piani Migliori')}</>
            )}
          </button>
        </motion.div>

        {/* Results */}
        <AnimatePresence>
          {results && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="trip-results">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Plane className="w-5 h-5 text-teal-400" />
                  {t('tripPlanner.yourItinerary', 'Il tuo Itinerario')}
                </h2>
                <div className="bg-teal-500/10 border border-teal-500/20 rounded-full px-3 py-1">
                  <span className="text-teal-400 text-sm font-bold">{results.stop_count} {t('tripPlanner.stops', 'tappe')}</span>
                </div>
              </div>

              {/* Stop Cards */}
              <div className="space-y-3 mb-6">
                {results.stops.map((stop, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`relative bg-white/[0.03] border rounded-xl overflow-hidden ${
                      stop.found ? 'border-white/10' : 'border-red-500/20'
                    }`}
                    data-testid={`stop-card-${idx}`}
                  >
                    {/* Connection line */}
                    {idx < results.stops.length - 1 && (
                      <div className="absolute -bottom-3 left-8 w-px h-6 bg-teal-500/20 z-10" />
                    )}

                    <div className="flex items-center gap-4 p-4">
                      {/* Stop number + flag */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center overflow-hidden">
                          {stop.flag_url ? (
                            <img src={stop.flag_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.innerHTML = `<span class="text-teal-400 font-bold text-sm">${idx+1}</span>`; }} />
                          ) : (
                            <span className="text-teal-400 font-bold text-sm">{idx + 1}</span>
                          )}
                        </div>
                      </div>

                      {/* Destination info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm">{stop.country || stop.input}</p>
                          {stop.input.toLowerCase() !== stop.country?.toLowerCase() && (
                            <span className="text-slate-500 text-xs">({stop.input})</span>
                          )}
                        </div>
                        {stop.found && stop.recommended_plan ? (
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <Database className="w-3 h-3" /> {stop.recommended_plan.data}
                            </span>
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {stop.recommended_plan.duration}
                            </span>
                          </div>
                        ) : (
                          <p className="text-red-400/70 text-xs mt-1">{t('tripPlanner.notFound', 'Destinazione non trovata nel catalogo')}</p>
                        )}
                      </div>

                      {/* Price */}
                      {stop.found && stop.recommended_plan && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-teal-400 font-bold text-lg font-mono">{formatPrice(parseFloat(stop.recommended_plan.price))}</p>
                          <p className="text-slate-500 text-[10px]">{stop.variations_count} {t('tripPlanner.plansAvailable', 'piani')}</p>
                        </div>
                      )}

                      {/* Arrow connector */}
                      {idx < results.stops.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-teal-500/30 absolute -right-2 top-1/2 -translate-y-1/2 hidden sm:block" />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Total + Add All */}
              {results.total_price > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-r from-teal-600/10 to-cyan-600/10 border border-teal-500/20 rounded-2xl p-5"
                  data-testid="trip-total"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">{t('tripPlanner.bundleTotal', 'Totale Bundle')}</p>
                      <p className="text-white text-2xl font-bold font-mono mt-1">{formatPrice(results.total_price)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500 text-xs">{results.stop_count} eSIM</p>
                      <p className="text-slate-500 text-xs">{daysPerStop} {t('tripPlanner.daysEach', 'giorni/tappa')}</p>
                    </div>
                  </div>
                  <button
                    onClick={addedAll ? () => onNavigate('checkout') : handleAddAllToCart}
                    className={`w-full font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                      addedAll
                        ? 'bg-green-500 hover:bg-green-400 text-black'
                        : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-black'
                    }`}
                    data-testid="add-all-to-cart-btn"
                  >
                    {addedAll ? (
                      <><Check className="w-4 h-4" /> {t('tripPlanner.goToCheckout', 'Vai al Checkout')}</>
                    ) : (
                      <><ShoppingCart className="w-4 h-4" /> {t('tripPlanner.addAllToCart', 'Aggiungi Tutto al Carrello')}</>
                    )}
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
